import { feedLabel } from '@/lib/feedTypes';
import { LateChip } from '@/components/entry/LateChip';
import { Fragment, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useEnteredBy } from '@/hooks/useEnteredBy';
import { useMembership } from '@/hooks/useMembership';
import { toNumberOrNull } from '@/lib/number';
import { cropFocusDate, cropPhase, daysBetween, docOn, type Crop } from '@/lib/crop';
import { FeedingSummary } from '@/components/pond/FeedingSummary';
import { HarvestList } from '@/components/pond/HarvestList';
import { EmptyNote, Section } from './shared';
import { useCropFeedingsOn, useCropHarvests, useCropSamplings, usePondDailyFeed } from './data';
import { abwText, FEED_SLOTS, headerActionClass } from './format';
import { Term } from '@/components/ui/Term';

const num = (v: string | number | null | undefined): number | null => toNumberOrNull(v) ?? null;
const th = 'px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500';
const td = 'px-2.5 py-2 tabular-nums';

function FeedingEntries({ farmId, cropId, date }: { farmId: string; cropId: string; date: string }) {
  const { data, isLoading, isError, refetch } = useCropFeedingsOn(farmId, cropId, date);
  const enteredBy = useEnteredBy(farmId);
  if (isError)
    return (
      <p className="py-1 text-[13px] text-red-700">
        Couldn't load this day's entries.{' '}
        <button type="button" onClick={() => refetch()} className="font-semibold underline">
          Try again
        </button>
      </p>
    );
  const feedings = [...(data ?? [])].sort((a, b) => FEED_SLOTS.indexOf(a.feeding_slot) - FEED_SLOTS.indexOf(b.feeding_slot));
  if (feedings.length === 0) return <p className="py-1 text-[13px] text-gray-500">{isLoading ? 'Loading…' : 'No entries.'}</p>;
  return (
    <ul className="flex flex-col gap-1.5 py-1 text-[13px]">
      {feedings.map((f) => {
        const entered = enteredBy(f, f.feeding_date);
        return (
          <li key={f.id}>
            <div className="flex items-baseline gap-2">
              <b className="w-12 shrink-0 text-gray-900">{f.feeding_slot}</b>
              <span className="font-semibold tabular-nums">{(num(f.feed_amount_kg) ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg</span>
              {feedLabel(f) && <span className="text-gray-500" data-feed-label>{feedLabel(f)}</span>}
              <LateChip row={f} entryDate={f.feeding_date} />
            </div>
            {entered && <div className="pl-14 text-xs text-gray-500">Entered by {entered}</div>}
          </li>
        );
      })}
    </ul>
  );
}

export function FeedingTab({ farmId, pondId, crop }: { farmId: string; pondId: string; crop: Crop }) {
  const { isSupervisorOrAbove, isLoading: membershipLoading } = useMembership();
  // Reserve the toggle's row height until the role is known, so rows don't jump.
  const tall = isSupervisorOrAbove || membershipLoading;
  const [openDay, setOpenDay] = useState<string | null>(null);
  const end = cropFocusDate(crop);
  // The daily summary is per pond, so it needs both ends of the crop to keep other crops out.
  const bounded = !!crop.stocking_date && !!end;
  const { data: days = [], isLoading } = usePondDailyFeed(farmId, pondId, crop.stocking_date ?? '', end ?? '', bounded);

  return (
    <div className="flex flex-wrap items-start gap-5">
      <Section labelledBy="fd-log" title="Daily feeding" className="flex-[2_1_520px]">
        {!bounded ? (
          <EmptyNote>This crop has no recorded end date, so its daily feeding can't be told apart from the pond's later crops.</EmptyNote>
        ) : days.length === 0 ? (
          <EmptyNote>{isLoading ? 'Loading…' : 'No feedings logged for this crop yet.'}</EmptyNote>
        ) : (
          <div className="relative max-h-[640px] overflow-auto">
            <table className="w-full min-w-[420px] border-collapse text-left text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-100">
                  <th className={th}>Date</th>
                  <th className={`${th} text-right`}><Term term="DOC" /></th>
                  <th className={`${th} text-right`}>Feed</th>
                  <th className={`${th} text-right`}>Feedings</th>
                  <th className={th}>Feed type</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  const open = openDay === d.feeding_date;
                  const label = format(parseISO(d.feeding_date), 'EEE, MMM d');
                  return (
                    <Fragment key={d.feeding_date}>
                      <tr className="border-b border-gray-50 last:border-0">
                        <td className={cn(td, tall && 'p-0')}>
                          {isSupervisorOrAbove ? (
                            <button
                              type="button"
                              aria-expanded={open}
                              aria-label={`${label}, who entered each feeding`}
                              onClick={() => setOpenDay(open ? null : d.feeding_date)}
                              className="flex min-h-[44px] w-full items-center gap-1 px-2.5 text-left font-medium text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                            >
                              {label}
                              <ChevronDown className={cn('h-3.5 w-3.5 text-gray-400 transition-transform', open && 'rotate-180')} />
                            </button>
                          ) : tall ? (
                            <div className="flex min-h-[44px] items-center px-2.5">{label}</div>
                          ) : (
                            label
                          )}
                        </td>
                        <td className={`${td} text-right`}>{docOn(crop, d.feeding_date) ?? '—'}</td>
                        <td className={`${td} text-right font-semibold`}>
                          {(num(d.total_kg) ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg
                        </td>
                        <td className={`${td} text-right`}>{d.slot_count}</td>
                        <td className="px-2.5 py-2">{d.feed_type ?? '—'}</td>
                      </tr>
                      {open && (
                        <tr className="border-b border-gray-50 bg-gray-50/60">
                          <td colSpan={5} className="px-2.5 py-1.5">
                            <FeedingEntries farmId={farmId} cropId={crop.id} date={d.feeding_date} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      <div className="min-w-0 flex-[1_1_300px]">
        <FeedingSummary cycleId={crop.id} farmId={farmId} closed={cropPhase(crop) === 'closed'} />
      </div>
    </div>
  );
}

export function SamplingTab({ farmId, pondId, crop }: { farmId: string; pondId: string; crop: Crop }) {
  const { data: samplings = [], isLoading } = useCropSamplings(farmId, crop.id);
  const canLog = cropPhase(crop) === 'grow_out';

  return (
    <Section
      labelledBy="sm-log"
      title="Samplings"
      action={
        canLog && (
          <Link to="/entry/pond/$pondId/sampling" params={{ pondId }} search={{}} className={headerActionClass(true)}>
            Log sampling
          </Link>
        )
      }
    >
      {samplings.length === 0 ? (
        <EmptyNote>{isLoading ? 'Loading…' : 'No samplings logged for this crop yet.'}</EmptyNote>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className={th}>Date</th>
                <th className={`${th} text-right`}><Term term="DOC" /></th>
                <th className={`${th} text-right`}>Sample</th>
                <th className={`${th} text-right`}><Term term="ABW" /></th>
                <th className={`${th} text-right`}>Gain / day</th>
                <th className={`${th} text-right`}>Survival</th>
                <th className={`${th} text-right`}>Biomass</th>
              </tr>
            </thead>
            <tbody>
              {samplings.map((s, i) => {
                const abw = num(s.abw_g);
                const prev = samplings[i - 1];
                const prevAbw = prev ? num(prev.abw_g) : null;
                const gap = prev ? daysBetween(prev.sampling_date, s.sampling_date) : 0;
                const gain = abw !== null && prevAbw !== null && gap > 0 ? (abw - prevAbw) / gap : null;
                const survival = num(s.survival_rate_pct);
                const biomass = num(s.estimated_biomass_kg);
                return (
                  <tr key={s.id} className="border-b border-gray-50 last:border-0">
                    <td className={td}>{format(parseISO(s.sampling_date), 'MMM d, yyyy')}</td>
                    <td className={`${td} text-right`}>{docOn(crop, s.sampling_date) ?? '—'}</td>
                    <td className={`${td} text-right`}>{s.sample_count} pcs</td>
                    <td className={`${td} text-right font-semibold`}>{abw !== null ? abwText(abw) : '—'}</td>
                    <td className={`${td} text-right`}>{gain !== null ? `${gain >= 0 ? '+' : ''}${gain.toFixed(2)} g` : '—'}</td>
                    <td className={`${td} text-right`}>{survival !== null ? `${survival.toFixed(1)}%` : '—'}</td>
                    <td className={`${td} text-right`}>
                      {biomass !== null ? `${biomass.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

export function HarvestsTab({ farmId, pondId, crop }: { farmId: string; pondId: string; crop: Crop }) {
  const { data: harvests = [], isLoading } = useCropHarvests(farmId, crop.id);
  // The entry harvest page is supervisor+ (technicians see it read-only).
  const { isSupervisorOrAbove } = useMembership();
  const logLink =
    isSupervisorOrAbove && cropPhase(crop) === 'grow_out' ? (
      <Link to="/entry/pond/$pondId/harvest" params={{ pondId }} search={{}} className={headerActionClass(true)}>
        Log harvest
      </Link>
    ) : null;
  if (!isLoading && harvests.length === 0) {
    return (
      <Section labelledBy="hv-none" title="Harvests" action={logLink}>
        <EmptyNote>No harvests yet for this crop.</EmptyNote>
      </Section>
    );
  }
  return (
    <div className="flex max-w-2xl flex-col gap-3">
      {logLink && <div className="flex justify-end">{logLink}</div>}
      <HarvestList cycleId={crop.id} farmId={farmId} />
    </div>
  );
}
